import React, { useEffect, useState, useCallback } from "react";
import {
  Table,
  Tabs,
  Spin,
  Input,
  Dropdown,
  Menu,
  Tag,
  Modal,
  Form,
  Button,
  message,
  Select,
  Switch,
  // Space,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  getAdminWalletHistory,
  getCustomerWallets,
  processWalletRefund,
  processWalletReward,
  getCashbackRules,
  createCashbackRule,
  updateCashbackRule,
} from "../../../redux/Actions/walletAction";
import { getUserType } from "../../../redux/Actions/auth"; // To get target audience
import { getWalletReducer } from "../../../redux/Reducer/walletReducer";
import { getAuthReducer } from "../../../redux/Reducer/authReducer";
import { FormatNumber } from "../../../Utils/cartmgt";
import moment from "moment";
import { SearchIcon } from "../../../asset/icons/icons";
import {
  MoreOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  UndoOutlined,
  GiftOutlined,
  PlusOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { useDebounce } from "../../../Utils/useDebounce";

const { Option } = Select;

const usermanText = {
  width: "250px",
  flexShrink: 0,
  color: "#45464E",
  fontFamily: '"MADE TOMMY"',
  fontSize: "20px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "28px",
  marginBottom: "25px",
};

const tag = {
  padding: "3.867px 7.734px",
  alignItems: "center",
  gap: "0px",
  borderRadius: "348.021px",
  background: "#F4F1FD",
};

const WalletManagement = () => {
  const dispatch = useDispatch();

  // Wallet & Rule States
  const {
    adminWalletHistory,
    isLoadingAdminWalletHistory,
    adminWalletPagination,
    customerWallets,
    isLoadingCustomerWallets,
    customerWalletsPagination,
    isProcessingRefund,
    isProcessingReward,
    refundStatus,
    rewardStatus,
    cashbackRules,
    isLoadingRules,
    isSavingRule,
    ruleActionStatus,
  } = useSelector(getWalletReducer);

  // Auth state for User Types
  const { userTypeData } = useSelector(getAuthReducer);

  const [activeTab, setActiveTab] = useState("1");
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchText = useDebounce(searchQuery, 500);

  // Modal States
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [isRewardModalOpen, setIsRewardModalOpen] = useState(false);
  const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  const [refundForm] = Form.useForm();
  const [rewardForm] = Form.useForm();
  const [ruleForm] = Form.useForm();

  // Helper for formatting
  const FormatCurrency = (amount) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
    }).format(amount || 0);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setSearchQuery("");
  };

  const fetchData = useCallback(
    (page = 1) => {
      if (activeTab === "1") {
        dispatch(getCustomerWallets({ page, search: debouncedSearchText }));
      } else if (activeTab === "2") {
        dispatch(getAdminWalletHistory({ page, search: debouncedSearchText }));
      } else if (activeTab === "3") {
        dispatch(getCashbackRules());
        if (!userTypeData || userTypeData.length === 0) dispatch(getUserType());
      }
    },
    [dispatch, debouncedSearchText, activeTab, userTypeData],
  );

  useEffect(() => {
    fetchData(1);
  }, [fetchData]);

  // Handle closing refund/reward modals on success
  const currentCustomerPage = customerWalletsPagination?.current || 1;

  useEffect(() => {
    if (refundStatus) {
      setIsRefundModalOpen(false);
      fetchData(currentCustomerPage);
    }
  }, [refundStatus, fetchData, currentCustomerPage]);

  useEffect(() => {
    if (rewardStatus) {
      setIsRewardModalOpen(false);
      fetchData(currentCustomerPage);
    }
  }, [rewardStatus, fetchData, currentCustomerPage]);

  // Handle closing Rule modal on success
  useEffect(() => {
    if (ruleActionStatus) {
      setIsRuleModalOpen(false);
      ruleForm.resetFields();
      setEditingRule(null);
      dispatch(getCashbackRules());
    }
  }, [ruleActionStatus, ruleForm, dispatch]);

  const onChangePageCustomers = (pagination) => {
    dispatch(
      getCustomerWallets({
        page: pagination.current,
        search: debouncedSearchText,
      }),
    );
  };

  const onChangePageHistory = (pagination) => {
    dispatch(
      getAdminWalletHistory({
        page: pagination.current,
        search: debouncedSearchText,
      }),
    );
  };

  const handleMenuClick = (e) => {
    if (e.key === "refund") {
      refundForm.resetFields();
      setIsRefundModalOpen(true);
    } else if (e.key === "reward") {
      rewardForm.resetFields();
      setIsRewardModalOpen(true);
    }
  };

  const handleOpenRuleModal = (rule = null) => {
    setEditingRule(rule);
    if (rule) {
      ruleForm.setFieldsValue({
        ...rule,
        is_active: rule.is_active === 1 || rule.is_active === true,
      });
    } else {
      ruleForm.resetFields();
      ruleForm.setFieldsValue({
        is_active: true,
        trigger_type: "amount",
        reward_type: "percentage",
      });
    }
    setIsRuleModalOpen(true);
  };

  const onFinishRefund = (values) => {
    const targetUserId = selectedRecord?.user_id || selectedRecord?.id;
    if (!targetUserId) {
      message.error("Error: Could not identify the user for this record.");
      return;
    }
    dispatch(
      processWalletRefund({
        user_id: parseInt(targetUserId),
        amount: parseFloat(values.amount),
        order_reference: values.order_reference,
      }),
    );
  };

  const onFinishReward = (values) => {
    const targetUserId = selectedRecord?.user_id || selectedRecord?.id;
    if (!targetUserId) {
      message.error("Error: Could not identify the user for this record.");
      return;
    }
    dispatch(
      processWalletReward({
        user_id: parseInt(targetUserId),
        amount: parseFloat(values.amount),
      }),
    );
  };

  const onFinishRule = (values) => {
    const payload = {
      ...values,
      threshold: parseFloat(values.threshold),
      reward_value: parseFloat(values.reward_value),
      is_active: values.is_active ? 1 : 0,
    };

    if (editingRule) {
      dispatch(updateCashbackRule(editingRule.id, payload));
    } else {
      dispatch(createCashbackRule(payload));
    }
  };

  const actionMenuItems = (
    <Menu onClick={handleMenuClick}>
      <Menu.Item key="refund">
        <div className="d-flex gap-2 align-items-center text-warning">
          <UndoOutlined /> <span>Issue Refund</span>
        </div>
      </Menu.Item>
      <Menu.Item key="reward">
        <div className="d-flex gap-2 align-items-center text-success">
          <GiftOutlined /> <span>Send Reward</span>
        </div>
      </Menu.Item>
    </Menu>
  );

  const customerColumns = [
    {
      title: "Customer Info",
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span className="orderHistory-text fw-bold">
            {record.user_name || record.name || "N/A"}
          </span>
          <span style={{ fontSize: "12px", color: "#888" }}>
            {record.user_email || record.email}
          </span>
        </div>
      ),
    },
    {
      title: "Wallet ID",
      dataIndex: "uuid",
      render: (text) => (
        <span style={{ fontSize: "12px", color: "#888" }}>{text || "N/A"}</span>
      ),
    },
    {
      title: "Current Balance",
      dataIndex: "balance",
      render: (text) => (
        <span
          className="orderHistory-text fw-bold"
          style={{ fontSize: "16px" }}
        >
          {FormatCurrency(text)}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text) => {
        let color = text === "active" ? "green" : "red";
        return (
          <Tag color={color} style={{ textTransform: "capitalize" }}>
            {text || "Unknown"}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      render: (text, record) => (
        <Dropdown
          overlay={actionMenuItems}
          trigger={["click"]}
          placement="bottomLeft"
          arrow
          onVisibleChange={(visible) => {
            if (visible) setSelectedRecord(record);
          }}
        >
          <span
            onClick={(e) => e.preventDefault()}
            style={{
              cursor: "pointer",
              padding: "5px 10px",
              border: "1px solid #ddd",
              borderRadius: "5px",
            }}
          >
            Manage <MoreOutlined />
          </span>
        </Dropdown>
      ),
    },
  ];

  const historyColumns = [
    {
      title: "Date",
      dataIndex: "created_at",
      render: (text) => (
        <span className="orderHistory-text">
          {moment(text).format("MMM Do YYYY, h:mm A")}
        </span>
      ),
    },
    {
      title: "Customer",
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span className="orderHistory-text fw-bold">{record.user_name}</span>
          <span style={{ fontSize: "12px", color: "#888" }}>
            {record.user_email}
          </span>
        </div>
      ),
    },
    {
      title: "Reference",
      dataIndex: "reference_id",
      render: (text) => <span className="orderHistory-text">{text}</span>,
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (text) => {
        const safeText = String(text).toLowerCase();
        const isCredit =
          safeText === "deposit" ||
          safeText === "credit" ||
          safeText === "reward" ||
          safeText === "refund";
        return (
          <span
            style={{
              color: isCredit ? "#52c41a" : "#ff4d4f",
              fontWeight: 500,
              textTransform: "capitalize",
            }}
          >
            {isCredit ? (
              <ArrowDownOutlined className="mr-1" />
            ) : (
              <ArrowUpOutlined className="mr-1" />
            )}
            {safeText}
          </span>
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      render: (text, record) => {
        const safeText = String(record.type).toLowerCase();
        const isCredit =
          safeText === "deposit" ||
          safeText === "credit" ||
          safeText === "reward" ||
          safeText === "refund";
        return (
          <span
            className="orderHistory-text fw-bold"
            style={{ color: isCredit ? "#52c41a" : "#ff4d4f" }}
          >
            {isCredit ? "+" : "-"}
            {FormatNumber(text)}
          </span>
        );
      },
    },
    {
      title: "Wallet Balance",
      dataIndex: "wallet_balance",
      render: (text) => (
        <span className="orderHistory-text">{FormatNumber(text)}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text) => {
        let color =
          text === "completed"
            ? "green"
            : text === "pending"
              ? "orange"
              : "red";
        return (
          <Tag color={color} style={{ textTransform: "capitalize" }}>
            {text}
          </Tag>
        );
      },
    },
  ];

  const ruleColumns = [
    {
      title: "Rule Name",
      dataIndex: "name",
      render: (text) => <span className="fw-bold">{text}</span>,
    },
    {
      title: "Target Audience",
      dataIndex: "user_type_id",
      render: (id) => {
        const type = userTypeData?.find((u) => u.id === id);
        return type ? type.name : `Type ${id}`;
      },
    },
    {
      title: "Trigger",
      render: (_, record) => (
        <div style={{ textTransform: "capitalize" }}>
          {record.trigger_type.replace("_", " ")} {" >="} {record.threshold}
        </div>
      ),
    },
    {
      title: "Reward",
      render: (_, record) => (
        <Tag color="gold">
          {record.reward_value}
          {record.reward_type === "percentage" ? "%" : " NGN"} Cashback
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      render: (active) => (
        <Tag color={active ? "green" : "red"}>
          {active ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Action",
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => handleOpenRuleModal(record)}
          icon={<EditOutlined />}
        >
          Edit
        </Button>
      ),
    },
  ];

  const items = [
    {
      key: "1",
      label: (
        <div style={{ color: "#252A4D" }}>
          Customer Wallets
          <span className="mx-2" style={tag}>
            {customerWalletsPagination?.total || 0}
          </span>
        </div>
      ),
      children: (
        <div className="row mb-4">
          <Spin spinning={isLoadingCustomerWallets} tip="Loading Customers...">
            <Table
              columns={customerColumns}
              dataSource={Array.isArray(customerWallets) ? customerWallets : []}
              onChange={onChangePageCustomers}
              scroll={{ x: 1000 }}
              rowKey="id"
              pagination={{
                current: customerWalletsPagination?.current,
                pageSize: customerWalletsPagination?.pageSize,
                total: customerWalletsPagination?.total,
                showSizeChanger: false,
                style: {
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "20px",
                },
              }}
            />
          </Spin>
        </div>
      ),
    },
    {
      key: "2",
      label: (
        <div style={{ color: "#252A4D" }}>
          Transaction Logs
          <span className="mx-2" style={tag}>
            {adminWalletPagination?.total || 0}
          </span>
        </div>
      ),
      children: (
        <div className="row mb-4">
          <Spin spinning={isLoadingAdminWalletHistory} tip="Loading History...">
            <Table
              columns={historyColumns}
              dataSource={
                Array.isArray(adminWalletHistory) ? adminWalletHistory : []
              }
              onChange={onChangePageHistory}
              scroll={{ x: 1200 }}
              rowKey="id"
              pagination={{
                current: adminWalletPagination?.current,
                pageSize: adminWalletPagination?.pageSize,
                total: adminWalletPagination?.total,
                showSizeChanger: false,
                style: {
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "20px",
                },
              }}
            />
          </Spin>
        </div>
      ),
    },
    {
      key: "3",
      label: <div style={{ color: "#252A4D" }}>Cashback Rules</div>,
      children: (
        <div className="row mb-4">
          <div className="d-flex justify-content-end mb-3">
            <Button
              type="primary"
              onClick={() => handleOpenRuleModal()}
              icon={<PlusOutlined />}
              style={{ backgroundColor: "#252A4D", height: "40px" }}
            >
              Create New Rule
            </Button>
          </div>
          <Spin spinning={isLoadingRules}>
            <Table
              columns={ruleColumns}
              dataSource={Array.isArray(cashbackRules) ? cashbackRules : []}
              rowKey="id"
              scroll={{ x: 900 }}
              pagination={false}
            />
          </Spin>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="p-3 mt-5" style={{ borderRadius: "0px" }}>
        <div className="row">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <span style={usermanText}>Wallet Management</span>
          </div>
        </div>

        {activeTab !== "3" && (
          <div className="d-flex justify-content-between">
            <div className="col-12 col-sm-6 col-md-5">
              <Input
                placeholder={
                  activeTab === "1"
                    ? "Search by Customer Name or Email"
                    : "Search by Reference, Name, or Email"
                }
                style={{
                  width: "100%",
                  border: "none",
                  backgroundColor: "#F7F7F7",
                  cursor: "text",
                  height: "44px",
                }}
                value={searchQuery}
                onChange={handleSearch}
                prefix={<SearchIcon />}
              />
            </div>
          </div>
        )}

        <Tabs
          className="border p-2 mt-3 overflow-x-hidden"
          defaultActiveKey="1"
          activeKey={activeTab}
          onChange={handleTabChange}
          items={items}
        />
      </div>

      {/* --- REFUND MODAL --- */}
      <Modal
        title="Process Refund to Wallet"
        open={isRefundModalOpen}
        onCancel={() => setIsRefundModalOpen(false)}
        footer={null}
      >
        <div
          style={{
            padding: "12px",
            backgroundColor: "#F8F9FD",
            borderRadius: "8px",
            marginBottom: "20px",
            border: "1px solid #E0E2E7",
          }}
        >
          <span style={{ color: "#888", fontSize: "12px" }}>
            Refunding to Customer:
          </span>
          <div
            style={{ fontWeight: "bold", color: "#252A4D", fontSize: "16px" }}
          >
            {selectedRecord?.user_name || selectedRecord?.name}
          </div>
          <div style={{ fontSize: "13px", color: "#555" }}>
            {selectedRecord?.user_email || selectedRecord?.email}
          </div>
        </div>

        <Form layout="vertical" form={refundForm} onFinish={onFinishRefund}>
          <Form.Item
            name="amount"
            label="Refund Amount (NGN)"
            rules={[{ required: true, message: "Amount is required" }]}
          >
            <Input
              type="number"
              placeholder="Enter Amount"
              prefix="₦"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="order_reference"
            label="Original Order Reference"
            rules={[{ required: true, message: "Order reference is required" }]}
            help="The order ID related to this refund."
          >
            <Input placeholder="e.g. NB_884933" size="large" />
          </Form.Item>

          <Button
            type="primary"
            htmlType="submit"
            block
            loading={isProcessingRefund}
            style={{
              backgroundColor: "#252A4D",
              height: "45px",
              fontSize: "16px",
            }}
          >
            Issue Refund
          </Button>
        </Form>
      </Modal>

      {/* --- REWARD MODAL --- */}
      <Modal
        title="Send Wallet Reward"
        open={isRewardModalOpen}
        onCancel={() => setIsRewardModalOpen(false)}
        footer={null}
      >
        <div
          style={{
            padding: "12px",
            backgroundColor: "#F8F9FD",
            borderRadius: "8px",
            marginBottom: "20px",
            border: "1px solid #E0E2E7",
          }}
        >
          <span style={{ color: "#888", fontSize: "12px" }}>
            Rewarding Customer:
          </span>
          <div
            style={{ fontWeight: "bold", color: "#252A4D", fontSize: "16px" }}
          >
            {selectedRecord?.user_name || selectedRecord?.name}
          </div>
          <div style={{ fontSize: "13px", color: "#555" }}>
            {selectedRecord?.user_email || selectedRecord?.email}
          </div>
        </div>

        <Form layout="vertical" form={rewardForm} onFinish={onFinishReward}>
          <Form.Item
            name="amount"
            label="Reward Amount (NGN)"
            rules={[{ required: true, message: "Amount is required" }]}
          >
            <Input
              type="number"
              placeholder="Enter Reward Amount"
              prefix="₦"
              size="large"
            />
          </Form.Item>

          <Button
            type="primary"
            htmlType="submit"
            block
            loading={isProcessingReward}
            style={{
              backgroundColor: "#52c41a",
              height: "45px",
              borderColor: "#52c41a",
              fontSize: "16px",
            }}
          >
            Send Reward
          </Button>
        </Form>
      </Modal>

      {/* --- CASHBACK RULE MODAL --- */}
      <Modal
        title={editingRule ? "Edit Cashback Rule" : "Create Cashback Rule"}
        open={isRuleModalOpen}
        onCancel={() => setIsRuleModalOpen(false)}
        footer={null}
      >
        <Form layout="vertical" form={ruleForm} onFinish={onFinishRule}>
          <Form.Item
            name="name"
            label="Rule Name"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input placeholder="e.g. Distributor Gold Tier" size="large" />
          </Form.Item>

          <Form.Item
            name="user_type_id"
            label="Target Audience (User Type)"
            rules={[{ required: true, message: "Required" }]}
          >
            <Select placeholder="Select User Type" size="large">
              {userTypeData?.slice(2).map((type) => (
                <Option key={type.id} value={type.id}>
                  {type.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <div className="row">
            <div className="col-md-6">
              <Form.Item
                name="trigger_type"
                label="Trigger Condition"
                rules={[{ required: true }]}
              >
                <Select size="large">
                  <Option value="amount">Purchase Amount</Option>
                  <Option value="order_count">Order Count</Option>
                </Select>
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item
                name="threshold"
                label="Threshold Value"
                rules={[{ required: true, message: "Required" }]}
              >
                <Input type="number" size="large" placeholder="e.g. 50000" />
              </Form.Item>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <Form.Item
                name="reward_type"
                label="Reward Type"
                rules={[{ required: true }]}
              >
                <Select size="large">
                  <Option value="percentage">Percentage (%)</Option>
                  <Option value="fixed">Fixed Amount (NGN)</Option>
                </Select>
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item
                name="reward_value"
                label="Reward Value"
                rules={[{ required: true, message: "Required" }]}
              >
                <Input type="number" size="large" placeholder="e.g. 10" />
              </Form.Item>
            </div>
          </div>

          <Form.Item name="is_active" label="Status" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Button
            type="primary"
            htmlType="submit"
            block
            loading={isSavingRule}
            style={{
              backgroundColor: "#252A4D",
              height: "45px",
              fontSize: "16px",
            }}
          >
            {editingRule ? "Update Rule" : "Create Rule"}
          </Button>
        </Form>
      </Modal>
    </>
  );
};

export default WalletManagement;
